import React from 'react';
import { Link } from 'react-router-dom';
import '../components/css/candidatedash.css';
import styled from '@emotion/styled';
import { useSelector } from "react-redux";
import {
  Typography,
  Box,
  Container as MuiContainer,
  Button as MuiButton,
  useTheme
} from '@mui/material';

export default function CandidateHomeContent({ collapsed }) {
  const { isLoggedIn, user } = useSelector(state => state.auth);
  const theme = useTheme();

  const authUser = isLoggedIn && user?.data?.[0];
  const userType = authUser?.type;
  
  const isClient = isLoggedIn && (userType === "Client" || userType === "ClientAdmin" || userType === "manager" || userType === "customer");

  const heroTitle = "Welcome to Artemy.ai!";
  const heroSub = "THE SECURE CONTROL PLANE FOR HUMAN + AI SOFTWARE DELIVERY";

  const renderManagerActions = () => {
    if (!isClient) return null;

    return (
      <Box sx={{ mt: 6, display: 'flex', flexDirection: 'column', gap: 3, width: '100%', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255,255,255,0.9)' }}>
          Get Started
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
          {[
            { id: 1, title: 'Create Solution', path: '/CreateSolution' },
            { id: 2, title: 'Create ART', path: '/project-requirment' },
            { id: 3, title: 'Create POD', path: '/client-createpod' }
          ].map(step => (
            <Link key={step.id} to={step.path} style={{ textDecoration: 'none' }}>
              <PremiumButton sx={{ minWidth: '200px' }}>
                {step.title}
              </PremiumButton>
            </Link>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Container>
      <HeroOuter>
        <MuiContainer maxWidth="lg" className="artemy-fade-up">
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <GradientText variant="h1" className="artemy-fade-up" sx={{ fontSize: { xs: '3rem', md: '5rem' } }}>
              {heroTitle}
            </GradientText>
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                mb: 5,
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              {heroSub}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {isLoggedIn ? (
                <Link to={isClient ? '/client-dashboard' : '/candidate-dashboard'} style={{ textDecoration: 'none' }}>
                  <PremiumButton size="large">
                    Go to Dashboard
                  </PremiumButton>
                </Link>
              ) : (
                <Link to="/register" style={{ textDecoration: 'none' }}>
                  <PremiumButton size="large">
                    Register Now
                  </PremiumButton>
                </Link>
              )}
            </Box>

            {renderManagerActions()}
          </Box>
        </MuiContainer>
      </HeroOuter>

      <FooterSection>
        <MuiContainer maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: 1 }}>ARTEMY<span style={{ color: '#3b82f6' }}>.AI</span></Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1 }}>Revolutionizing global software delivery.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <a href="https://artemy.ai/" style={{ color: "rgba(255,255,255,0.6)", textDecoration: 'none' }}>Privacy Policy</a>
              <a href="https://artemy.ai/" style={{ color: "rgba(255,255,255,0.6)", textDecoration: 'none' }}>Terms of Service</a>
            </Box>
          </Box>
          <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              &copy; {new Date().getFullYear()} Artemy.ai. All rights reserved.
            </Typography>
          </Box>
        </MuiContainer>
      </FooterSection>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: #0f172a;
  color: #ffffff;
  overflow-x: hidden;
`;

const HeroOuter = styled.div`
  background: radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.15) 0%, rgba(15, 23, 42, 0) 70%);
  padding: 120px 0 80px 0;
  position: relative;
  flex-grow: 1;
  display: flex;
  align-items: center;
  
  @media (max-width: 900px) {
    padding: 80px 0 40px 0;
  }
`;

const GradientText = styled(Typography)`
  background: linear-gradient(135deg, #ffffff 0%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800 !important;
  margin-bottom: 24px !important;
`;

const PremiumButton = styled(MuiButton)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 12px 32px;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  font-size: 1.1rem;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.5);
    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  }
`;

const FooterSection = styled.footer`
  padding: 80px 0 40px 0;
  background: #080d1a;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;
